import React, { useCallback, useEffect, useRef, useState } from 'react';
import { notificationAPI } from '../../services/api';

const TYPE_ICON = {
  NURSE_ASSIGNED: '🚑',
  NURSE_EN_ROUTE: '📍',
  VISIT_COMPLETED: '✅',
  REPORT_AVAILABLE: '📋',
  PAYMENT_DUE: '💰',
  APPOINTMENT_REMINDER: '📅',
  CARE_PLAN_UPDATED: '📝',
  REFERRAL_STATUS: '🔁',
  SYSTEM_ALERT: '🔔',
  CONSENT_EXPIRING: '⚠️',
};

const timeAgo = (iso) => {
  if (!iso) return '';
  const s = (Date.now() - new Date(iso).getTime()) / 1000;
  if (s < 60) return 'just now';
  if (s < 3600) return `${Math.floor(s / 60)}m ago`;
  if (s < 86400) return `${Math.floor(s / 3600)}h ago`;
  return `${Math.floor(s / 86400)}d ago`;
};

/**
 * Header bell — unread badge polls every 20s; opening the panel loads the
 * latest 15. Clicking an item marks it read; "Mark all read" clears.
 */
export default function NotificationBell() {
  const [count, setCount] = useState(0);
  const [open, setOpen] = useState(false);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const wrapRef = useRef(null);

  const refreshCount = useCallback(() => {
    notificationAPI.unreadCount()
      .then(r => setCount((r?.data ?? r)?.unread_count ?? 0))
      .catch(() => {});
  }, []);

  useEffect(() => {
    refreshCount();
    // 20s cadence so field events (new requests, visit updates) surface fast.
    const t = setInterval(refreshCount, 20000);
    return () => clearInterval(t);
  }, [refreshCount]);

  // close on outside click
  useEffect(() => {
    if (!open) return;
    const onDoc = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, [open]);

  const openPanel = () => {
    setOpen(o => !o);
    if (!open) {
      setLoading(true);
      notificationAPI.list({ page: 1, per_page: 15 })
        .then(r => setItems(r?.data || []))
        .catch(() => setItems([]))
        .finally(() => setLoading(false));
    }
  };

  const readOne = (n) => {
    if (!n.is_read) {
      notificationAPI.markRead(n.notification_id).catch(() => {});
      setItems(list => list.map(x => x.notification_id === n.notification_id ? { ...x, is_read: true } : x));
      setCount(c => Math.max(0, c - 1));
    }
  };

  const readAll = () => {
    notificationAPI.markAllRead().catch(() => {});
    setItems(list => list.map(x => ({ ...x, is_read: true })));
    setCount(0);
  };

  return (
    <div ref={wrapRef} style={{ position: 'relative' }}>
      <button
        onClick={openPanel}
        aria-label={`Notifications${count ? ` (${count} unread)` : ''}`}
        style={{
          position: 'relative', background: '#f6eff2', border: '1px solid #ece3e7',
          borderRadius: '50%', width: 32, height: 32, cursor: 'pointer',
          display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 15,
        }}
      >
        🔔
        {count > 0 && (
          <span style={{
            position: 'absolute', top: -4, right: -4, minWidth: 16, height: 16,
            borderRadius: 999, background: '#DC2626', color: '#fff',
            fontSize: 9.5, fontWeight: 800, display: 'flex', alignItems: 'center',
            justifyContent: 'center', padding: '0 4px', border: '2px solid #fff',
          }}>
            {count > 99 ? '99+' : count}
          </span>
        )}
      </button>

      {open && (
        <div style={{
          position: 'absolute', right: 0, top: 40, width: 360, maxWidth: '90vw',
          background: '#fff', border: '1px solid #ECE3E7', borderRadius: 14,
          boxShadow: '0 16px 40px -12px rgba(13,27,46,0.25)', zIndex: 1000,
          fontFamily: 'Inter, sans-serif',
        }}>
          <div style={{
            display: 'flex', justifyContent: 'space-between', alignItems: 'center',
            padding: '12px 16px', borderBottom: '1px solid #F1F5F9',
          }}>
            <span style={{ fontSize: 13.5, fontWeight: 800, color: '#2b1b22' }}>Notifications</span>
            {count > 0 && (
              <button onClick={readAll} style={{
                background: 'none', border: 'none', cursor: 'pointer',
                fontSize: 11.5, fontWeight: 700, color: '#7b1e3c',
              }}>Mark all read</button>
            )}
          </div>
          <div style={{ maxHeight: 420, overflowY: 'auto' }}>
            {loading ? (
              <div style={{ padding: 20, fontSize: 12.5, color: '#a99aa0' }}>Loading…</div>
            ) : items.length === 0 ? (
              <div style={{ padding: '28px 16px', textAlign: 'center', fontSize: 12.5, color: '#a99aa0' }}>
                You're all caught up.
              </div>
            ) : items.map(n => (
              <div
                key={n.notification_id}
                onClick={() => readOne(n)}
                style={{
                  display: 'flex', gap: 10, padding: '11px 16px', cursor: 'pointer',
                  background: n.is_read ? '#fff' : '#f7eef3',
                  borderBottom: '1px solid #F1F5F9',
                }}
              >
                <span style={{ fontSize: 16, flexShrink: 0 }}>{TYPE_ICON[n.type] || '🔔'}</span>
                <div style={{ minWidth: 0, flex: 1 }}>
                  <div style={{ fontSize: 12.5, fontWeight: n.is_read ? 600 : 800, color: '#2b1b22' }}>
                    {n.title || n.type}
                  </div>
                  <div style={{ fontSize: 12, color: '#8b7681', lineHeight: 1.45, marginTop: 2 }}>
                    {n.message}
                  </div>
                  <div style={{ fontSize: 10.5, color: '#a99aa0', marginTop: 3 }}>{timeAgo(n.sent_at)}</div>
                </div>
                {!n.is_read && (
                  <span style={{ width: 8, height: 8, borderRadius: '50%', background: '#7b1e3c', flexShrink: 0, marginTop: 5 }} />
                )}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
